import { useState, useCallback, useRef, useEffect } from 'react';
import weddingEventService from '../../../services/weddingEventService';
import { DEFAULT_VENUE, GRID_SIZE, PALETTE_MODULES, ZOOM_MIN, ZOOM_MAX } from './constants';

/**
 * Snap a value to the nearest grid point.
 */
function snapToGrid(value) {
  return Math.round(value / GRID_SIZE) * GRID_SIZE;
}

/**
 * Generate a simple unique ID for non-table elements.
 */
function generateId() {
  return `el-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

/**
 * Merge DB tables into layout.tables, filling in missing positions automatically.
 */
function mergeTablesWithLayout(dbTables, layoutTables = {}) {
  const merged = { ...layoutTables };
  let autoX = 60;
  let autoY = 60;

  dbTables.forEach((table) => {
    const key = String(table.id);
    if (!merged[key]) {
      merged[key] = {
        x: autoX,
        y: autoY,
        shape: table.tableType === 'Kerek' ? 'round'
          : table.tableType === 'Négyzet' ? 'square'
          : 'rect',
        width: table.tableType === 'Kerek' ? 100 : 160,
        height: table.tableType === 'Kerek' ? 100 : 80,
        rotation: 0,
      };
      autoX += 140;
      if (autoX > 600) { autoX = 60; autoY += 140; }
    }
  });

  // Remove layout entries for tables that no longer exist in DB
  const dbIds = new Set(dbTables.map(t => String(t.id)));
  Object.keys(merged).forEach(key => {
    if (!dbIds.has(key)) delete merged[key];
  });

  return merged;
}

/**
 * Central state hook for the Seating Planner.
 * Provides all data + actions consumed by SeatingTab and its children.
 */
export function useSeatingState(eventId) {
  const [layout, setLayout] = useState({
    venue: { ...DEFAULT_VENUE },
    tables: {},
    elements: [],
    companionSeats: {}, // { 'companion-{guestId}': { tableId, seatNumber } }
  });
  const [seatingChart, setSeatingChart] = useState({ tables: [], unassignedGuests: [] });
  const [allGuests, setAllGuests] = useState([]);
  const [selectedTableId, setSelectedTableId] = useState(null);
  const [highlightedGuestId, setHighlightedGuestId] = useState(null);
  const [activeFilter, setActiveFilter] = useState('none'); // 'none' | 'side' | 'category'
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDirty, setIsDirty] = useState(false);

  // Debounce timer for layout persistence
  const saveTimer = useRef(null);

  // Undo history – array of layout snapshots
  const layoutHistory = useRef([]);

  // ─── Data Fetching ───────────────────────────────────────────────────────────

  const fetchAll = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    setError(null);
    try {
      const [chartRes, layoutRes, guestsRes] = await Promise.all([
        weddingEventService.getSeatingChart(eventId),
        weddingEventService.getSeatingLayout(eventId),
        weddingEventService.getGuests(eventId),
      ]);

      const dbTables = chartRes?.data?.tables ?? [];
      const savedLayout = layoutRes?.data;
      const guests = guestsRes?.data ?? [];

      const mergedTables = mergeTablesWithLayout(
        dbTables,
        savedLayout?.tables ?? {}
      );

      const newLayout = {
        venue: savedLayout?.venue ?? { ...DEFAULT_VENUE },
        tables: mergedTables,
        elements: savedLayout?.elements ?? [],
        companionSeats: savedLayout?.companionSeats ?? {},
      };

      setLayout(newLayout);
      setSeatingChart({
        tables: dbTables,
        unassignedGuests: chartRes?.data?.unassignedGuests ?? [],
      });
      setAllGuests(guests);

      // If new DB tables were added without layout entries, save merged layout
      const hadNewTables = Object.keys(mergedTables).length !== Object.keys(savedLayout?.tables ?? {}).length;
      if (hadNewTables && dbTables.length > 0) {
        persistLayoutImmediate(newLayout);
      }
    } catch (err) {
      setError(err.message || 'Nem sikerült betölteni az ültetési rendet');
    } finally {
      setLoading(false);
    }
  }, [eventId]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Refresh only the seating chart (tables + unassigned guests) and guest list,
   * WITHOUT reloading the layout JSON from DB.
   * This avoids a race condition where in-flight layout mutations (e.g. companion
   * seat assignments) would be overwritten by a stale DB read.
   */
  const refreshChartOnly = useCallback(async () => {
    if (!eventId) return;
    try {
      const [chartRes, guestsRes] = await Promise.all([
        weddingEventService.getSeatingChart(eventId),
        weddingEventService.getGuests(eventId),
      ]);
      setSeatingChart({
        tables: chartRes?.data?.tables ?? [],
        unassignedGuests: chartRes?.data?.unassignedGuests ?? [],
      });
      setAllGuests(guestsRes?.data ?? []);
    } catch (err) {
      console.error('Refresh chart error:', err);
    }
  }, [eventId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // ─── Layout Persistence ──────────────────────────────────────────────────────

  const persistLayoutImmediate = useCallback(async (layoutToSave) => {
    if (!eventId) return;
    try {
      await weddingEventService.saveSeatingLayout(eventId, layoutToSave);
      setIsDirty(false);
    } catch (err) {
      console.error('Layout save error:', err);
    }
  }, [eventId]);

  const persistLayout = useCallback((newLayout) => {
    setIsDirty(true);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      persistLayoutImmediate(newLayout);
    }, 1000);
  }, [persistLayoutImmediate]);

  // ─── Layout Mutations ────────────────────────────────────────────────────────

  const updateLayout = useCallback((updater) => {
    setLayout(prev => {
      layoutHistory.current = [...layoutHistory.current.slice(-29), prev];
      const next = updater(prev);
      persistLayout(next);
      return next;
    });
  }, [persistLayout]);

  const undo = useCallback(() => {
    if (layoutHistory.current.length === 0) return;
    const prev = layoutHistory.current[layoutHistory.current.length - 1];
    layoutHistory.current = layoutHistory.current.slice(0, -1);
    setLayout(prev);
    persistLayout(prev);
  }, [persistLayout]);

  const setVenueSize = useCallback((width, height) => {
    updateLayout(prev => ({ ...prev, venue: { ...prev.venue, width, height } }));
  }, [updateLayout]);

  const setVenuePolygon = useCallback((polygon) => {
    updateLayout(prev => ({
      ...prev,
      venue: { ...prev.venue, polygon: polygon ?? null },
    }));
  }, [updateLayout]);

  const moveElement = useCallback((id, deltaX, deltaY, isTable) => {
    updateLayout(prev => {
      if (isTable) {
        const key = String(id);
        const el = prev.tables[key];
        if (!el) return prev;
        return {
          ...prev,
          tables: {
            ...prev.tables,
            [key]: {
              ...el,
              x: snapToGrid(el.x + deltaX),
              y: snapToGrid(el.y + deltaY),
            },
          },
        };
      } else {
        return {
          ...prev,
          elements: prev.elements.map(el =>
            el.id === id
              ? { ...el, x: snapToGrid(el.x + deltaX), y: snapToGrid(el.y + deltaY) }
              : el
          ),
        };
      }
    });
  }, [updateLayout]);

  const resizeElement = useCallback((id, nx, ny, nw, nh, isTable) => {
    const w = Math.max(60, snapToGrid(nw));
    const h = Math.max(40, snapToGrid(nh));
    const x = snapToGrid(nx);
    const y = snapToGrid(ny);
    updateLayout(prev => {
      if (isTable) {
        const key = String(id);
        return { ...prev, tables: { ...prev.tables, [key]: { ...prev.tables[key], x, y, width: w, height: h } } };
      } else {
        return { ...prev, elements: prev.elements.map(el => el.id === id ? { ...el, x, y, width: w, height: h } : el) };
      }
    });
  }, [updateLayout]);

  const renameElement = useCallback((id, label, isTable) => {
    if (isTable) {
      // Table name is stored in DB, not layout JSON — handled via updateSeatingTable
      return;
    }
    updateLayout(prev => ({
      ...prev,
      elements: prev.elements.map(el => el.id === id ? { ...el, label } : el),
    }));
  }, [updateLayout]);

  /**
   * Create a new table element:
   * 1. Creates DB record via API
   * 2. Adds position entry to layout JSON
   */
  const createTableOnCanvas = useCallback(async (moduleType, x, y) => {
    const module = PALETTE_MODULES.find(m => m.type === moduleType);
    if (!module || !module.isTable) return;

    const shape = moduleType === 'round_table' ? 'round' : 'rect';
    const typeLabel = moduleType === 'round_table' ? 'Kerek' : 'Négyszög';

    try {
      const res = await weddingEventService.addSeatingTable(eventId, {
        tableName: `${seatingChart.tables.length + 1}`,
        capacity: module.defaultCapacity,
        tableType: typeLabel,
      });
      const newTable = res?.data;
      if (!newTable) return;

      updateLayout(prev => {
        const newLayout = {
          ...prev,
          tables: {
            ...prev.tables,
            [String(newTable.id)]: {
              x: snapToGrid(x),
              y: snapToGrid(y),
              shape,
              width: module.defaultWidth,
              height: module.defaultHeight,
              rotation: 0,
            },
          },
        };
        return newLayout;
      });

      // Refresh chart so the new table appears with guestCount etc.
      // Use refreshChartOnly to avoid overwriting in-memory layout (companion seats).
      refreshChartOnly();
    } catch (err) {
      console.error('Create table error:', err);
    }
  }, [eventId, seatingChart.tables.length, updateLayout, refreshChartOnly]);

  /**
   * Create a non-table element (dancefloor, bar, etc.)
   */
  const createModuleOnCanvas = useCallback((moduleType, x, y) => {
    const module = PALETTE_MODULES.find(m => m.type === moduleType);
    if (!module || module.isTable) return;

    updateLayout(prev => ({
      ...prev,
      elements: [
        ...prev.elements,
        {
          id: generateId(),
          type: moduleType,
          label: module.label,
          x: snapToGrid(x),
          y: snapToGrid(y),
          width: module.defaultWidth,
          height: module.defaultHeight,
          rotation: 0,
        },
      ],
    }));
  }, [updateLayout]);

  const deleteElement = useCallback((id, isTable) => {
    if (isTable) {
      // Remove from DB + layout
      weddingEventService.deleteSeatingTable(eventId, parseInt(id))
        .then(() => {
          updateLayout(prev => {
            const newTables = { ...prev.tables };
            delete newTables[String(id)];
            // Also remove companion seat assignments for this table
            const tableIdNum = typeof id === 'number' ? id : parseInt(id, 10);
            const newCompanionSeats = {};
            Object.entries(prev.companionSeats ?? {}).forEach(([k, v]) => {
              if (v.tableId !== tableIdNum) newCompanionSeats[k] = v;
            });
            return { ...prev, tables: newTables, companionSeats: newCompanionSeats };
          });
          // Use refreshChartOnly to avoid overwriting in-memory layout (companion seats).
          refreshChartOnly();
        })
        .catch(err => console.error('Delete table error:', err));
    } else {
      updateLayout(prev => ({
        ...prev,
        elements: prev.elements.filter(el => el.id !== id),
      }));
    }
  }, [eventId, updateLayout, refreshChartOnly]);

  const renameTable = useCallback(async (tableId, newName) => {
    try {
      await weddingEventService.updateSeatingTable(eventId, tableId, { tableName: newName });
      refreshChartOnly();
    } catch (err) {
      console.error('Rename table error:', err);
    }
  }, [eventId, refreshChartOnly]);

  const updateTableCapacity = useCallback(async (tableId, capacity) => {
    try {
      await weddingEventService.updateSeatingTable(eventId, tableId, { capacity });
      refreshChartOnly();
    } catch (err) {
      console.error('Update capacity error:', err);
    }
  }, [eventId, refreshChartOnly]);

  // ─── Guest Assignment ─────────────────────────────────────────────────────────

  const assignGuestToSeat = useCallback(async (guestId, tableId, seatNumber = null) => {
    try {
      await weddingEventService.assignGuestToTable(eventId, guestId, tableId, seatNumber);
      await refreshChartOnly();
    } catch (err) {
      console.error('Assign guest error:', err);
      throw err;
    }
  }, [eventId, refreshChartOnly]);

  const unassignGuest = useCallback(async (guestId) => {
    try {
      await weddingEventService.unassignGuestFromTable(eventId, guestId);
      await refreshChartOnly();
    } catch (err) {
      console.error('Unassign guest error:', err);
    }
  }, [eventId, refreshChartOnly]);

  const assignCompanionToSeat = useCallback((companionId, tableId, seatNumber) => {
    updateLayout(prev => ({
      ...prev,
      companionSeats: {
        ...(prev.companionSeats ?? {}),
        [companionId]: { tableId, seatNumber },
      },
    }));
  }, [updateLayout]);

  const unassignCompanion = useCallback((companionId) => {
    updateLayout(prev => {
      const newSeats = { ...(prev.companionSeats ?? {}) };
      delete newSeats[companionId];
      return { ...prev, companionSeats: newSeats };
    });
  }, [updateLayout]);

  const updateRotation = useCallback((id, angle, isTable) => {
    updateLayout(prev => {
      if (isTable) {
        const key = String(id);
        return { ...prev, tables: { ...prev.tables, [key]: { ...prev.tables[key], rotation: angle } } };
      }
      return { ...prev, elements: prev.elements.map(el => el.id === id ? { ...el, rotation: angle } : el) };
    });
  }, [updateLayout]);

  /**
   * Delete all seating tables, clear all guest assignments, reset layout to blank.
   */
  const resetAll = useCallback(async () => {
    try {
      // Delete every table from the DB (which also unassigns guests server-side)
      const tables = seatingChart.tables ?? [];
      await Promise.all(tables.map(t => weddingEventService.deleteSeatingTable(eventId, t.id)));
      // Reset layout to empty
      const emptyLayout = {
        venue: { ...DEFAULT_VENUE },
        tables: {},
        elements: [],
        companionSeats: {},
      };
      setLayout(emptyLayout);
      layoutHistory.current = [];
      await persistLayoutImmediate(emptyLayout);
      await fetchAll();
    } catch (err) {
      console.error('Reset all error:', err);
    }
  }, [eventId, seatingChart.tables, persistLayoutImmediate, fetchAll]);

  const updateGuestCategory = useCallback(async (guestId, category) => {
    try {
      await weddingEventService.updateGuestCategory(eventId, guestId, category);
      await refreshChartOnly();
    } catch (err) {
      console.error('Update category error:', err);
    }
  }, [eventId, refreshChartOnly]);

  // ─── View Controls ────────────────────────────────────────────────────────────

  const clampZoom = useCallback((val) => {
    return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, val));
  }, []);

  const adjustZoom = useCallback((delta, pivotX, pivotY) => {
    setZoom(prev => {
      const next = clampZoom(prev * (delta > 0 ? 1.1 : 0.9));
      // Adjust pan so zoom is centered on pivot point
      if (pivotX !== undefined && pivotY !== undefined) {
        setPan(p => ({
          x: pivotX - (pivotX - p.x) * (next / prev),
          y: pivotY - (pivotY - p.y) * (next / prev),
        }));
      }
      return next;
    });
  }, [clampZoom]);

  /**
   * Pan/zoom so a given canvas coordinate is centered in the viewport.
   */
  const focusOnPosition = useCallback((canvasX, canvasY, viewportWidth, viewportHeight, targetZoom = 1.5) => {
    const nextZoom = clampZoom(targetZoom);
    setZoom(nextZoom);
    setPan({
      x: viewportWidth / 2 - canvasX * nextZoom,
      y: viewportHeight / 2 - canvasY * nextZoom,
    });
  }, [clampZoom]);

  // Derive unassigned companions from allGuests + layout.companionSeats
  const assignedCompanionIds = new Set(Object.keys(layout.companionSeats ?? {}));
  const unassignedCompanions = allGuests
    .filter(g => g.plusOne && g.plusOneName && !assignedCompanionIds.has(`companion-${g.id}`))
    .map(g => ({
      id: `companion-${g.id}`,
      firstName: g.plusOneName,
      lastName: '',
      isCompanion: true,
      parentGuestId: g.id,
      rsvpStatus: g.rsvpStatus,
      // Only inherit bride/groom side — other categories are personal and don't transfer
      guestCategory: (g.guestCategory === 'bride' || g.guestCategory === 'groom') ? g.guestCategory : null,
    }));

  return {
    // State
    layout,
    seatingChart,
    allGuests,
    unassignedCompanions,
    selectedTableId,
    setSelectedTableId,
    highlightedGuestId,
    setHighlightedGuestId,
    activeFilter,
    setActiveFilter,
    zoom,
    setZoom: (v) => setZoom(clampZoom(v)),
    pan,
    setPan,
    loading,
    error,
    isDirty,

    // Data actions
    fetchAll,
    refreshChartOnly,
    resetAll,
    undo,
    setVenueSize,
    setVenuePolygon,
    moveElement,
    resizeElement,
    renameElement,
    renameTable,
    updateTableCapacity,
    createTableOnCanvas,
    createModuleOnCanvas,
    deleteElement,
    updateRotation,
    assignGuestToSeat,
    unassignGuest,
    assignCompanionToSeat,
    unassignCompanion,
    updateGuestCategory,

    // View controls
    adjustZoom,
    focusOnPosition,
  };
}
